import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Parser from "html-react-parser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperclip,
  faTimes,
  faCheck,
  faPencilAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import * as Sentry from "@sentry/react";
import Button from "../../../../shared/button/Button";
import Sending from "../../../../shared/loaders/Sending";
import ModalThankYou from "../../../../shared/loaders/ModalThankYou";
import {
  Form,
  Label,
  Field,
  TextInput,
  ButtonsWrapper,
  FileInputLabel,
  FileInput,
  Filename,
  RemoveAttachment,
  Error,
  FinalSalaryWrapper,
  FinalSalaryTrigger,
  FinalSalaryNote,
  FinalSalaryInput,
  FinalSalaryInputAccept,
  EditFinalSalary,
  FakeButton,
} from "../styles/job-offer-styles";

export class Accept extends Component {
  constructor(props) {
    super(props);
    this.attachmentRef = React.createRef();

    this.state = {
      formLoading: false,
      successMessageActive: false,
      message: "",
      attachment: "",
      fileAttached: false,
      errors: null,
      finalSalaryInputActive: false,
      finalSalaryValue: undefined,
      finalSalaryAccepted: false,
    };
  }

  showSuccessMessage = () => {
    this.setState({ successMessageActive: true });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.setState({ formLoading: true });
    const { message, attachment, finalSalaryValue } = this.state;
    const { cardData, closeActionModalAfterSubmit, fetchKanbanData } =
      this.props;
    const { updateStatusLink } = cardData;
    const url = updateStatusLink;

    const formData = new FormData();

    formData.append("application_action[action]", "accepted_job");
    formData.append("application_action[final_salary]", finalSalaryValue);
    formData.append("application_action[message]", message);
    formData.append("application_action[attachment]", attachment);
    formData.append("application_action[action_at]", Date.now());

    const payload = {
      url,
      method: "post",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    };

    axios(payload)
      .then(() => {
        this.setState({ formLoading: false, errors: null });
        this.showSuccessMessage();
        setTimeout(() => {
          closeActionModalAfterSubmit();
          fetchKanbanData({ loading: false, updating: true });
        }, 2000);
      })
      .catch((error) => {
        this.setState({
          errors: error.response.data.errors,
          formLoading: false,
        });
        Sentry.captureException(error);
      });
  };

  handleMessage = (e) => {
    this.setState({ message: e.target.value });
  };

  handleAttachment = (e) => {
    this.setState({ attachment: e.target.files[0], fileAttached: true });
  };

  removeAttachment = () => {
    const node = this.attachmentRef.current;

    this.setState({ attachment: "", fileAttached: false });
    node.value = null;
  };

  showFinalSalaryInput = () => {
    this.setState({
      finalSalaryInputActive: true,
      finalSalaryAccepted: false,
    });
  };

  handleFinalSalaryInputValue = (e) =>
    this.setState({ finalSalaryValue: Number(e.target.value) });

  acceptFinalSalary = () => {
    this.setState({
      finalSalaryAccepted: true,
      finalSalaryInputActive: false,
    });
  };

  handleAcceptButton = (isSalaryFilled, finalSalaryAccepted) => {
    if (isSalaryFilled && finalSalaryAccepted) {
      return (
        <Button
          primary
          buttonName="Accept Job Offer"
          type="submit"
          dataTestId="accept-job-offer-submit-button"
        />
      );
    }
    return (
      <FakeButton type="button" data-test-id="accept-job-offer-fake-button">
        Accept Job Offer
      </FakeButton>
    );
  };

  render() {
    const {
      errors,
      message,
      fileAttached,
      successMessageActive,
      formLoading,
      finalSalaryInputActive,
      finalSalaryValue,
      finalSalaryAccepted,
    } = this.state;
    const { candidate, handleCancel, jobCurrencySymbol } = this.props;
    const isSalaryFilled =
      finalSalaryValue !== undefined && finalSalaryValue !== 0;

    return (
      <Fragment>
        {formLoading && <Sending white />}
        {successMessageActive && <ModalThankYou text="Job Offer Accepted" />}
        <Form onSubmit={this.handleSubmit} data-test-id="accept-job-offer-form">
          <Label
            className="animated fadeInUp delay-1"
            data-test-id="accept-job-offer-label"
          >
            You&apos;re about to <strong>accept</strong> the job offer for{" "}
            <strong>{candidate}</strong>.
          </Label>
          <FinalSalaryWrapper
            className="animated fadeInUp delay-2"
            data-test-id="accept-job-offer-final-salary-field"
          >
            {finalSalaryInputActive && (
              <Fragment>
                <FinalSalaryInput
                  type="number"
                  id="final-salary"
                  name="final-salary"
                  placeholder={`Final salary in ${jobCurrencySymbol}`}
                  onChange={this.handleFinalSalaryInputValue}
                  value={finalSalaryValue || ""}
                  autoFocus
                />
                <FinalSalaryInputAccept
                  type="button"
                  data-test-id="final-salary-accept"
                  onClick={this.acceptFinalSalary}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </FinalSalaryInputAccept>
              </Fragment>
            )}
            <FinalSalaryTrigger
              type="button"
              isSalaryFilled={isSalaryFilled}
              onClick={this.showFinalSalaryInput}
            >
              {isSalaryFilled
                ? `Final salary: ${jobCurrencySymbol}${finalSalaryValue}`
                : "Add final salary"}
              {isSalaryFilled && (
                <EditFinalSalary
                  data-tip="Edit final salary"
                  data-offset="{ 'left': -10 }"
                  data-for="edit-final-salary-tooltip"
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </EditFinalSalary>
              )}
            </FinalSalaryTrigger>
            {(!isSalaryFilled || !finalSalaryAccepted) && (
              <FinalSalaryNote
                className="animated fadeInUp delay-1"
                data-test-id="accept-job-offer-note"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                You must add <strong>final salary</strong> to accept this job
              </FinalSalaryNote>
            )}
          </FinalSalaryWrapper>
          <Field className="animated fadeInUp delay-3">
            <TextInput
              value={message}
              accept="true"
              data-test-id="accept-job-offer-message-field"
              placeholder="Please add your message..."
              onChange={this.handleMessage}
            />
            {errors && errors.message && (
              <Error className="animated bounceIn">
                Message {errors.message}.
              </Error>
            )}
          </Field>
          <Field className="animated fadeInUp delay-4">
            <FileInputLabel htmlFor="file" fileAttached={fileAttached}>
              <FontAwesomeIcon icon={faPaperclip} />
            </FileInputLabel>
            <FileInput
              type="file"
              name="file"
              id="file"
              data-test-id="accept-job-offer-file-input-field"
              ref={this.attachmentRef}
              onChange={this.handleAttachment}
            />
            <Filename>
              {fileAttached
                ? `${this.attachmentRef.current.files[0].name.replace(
                    "C:\\fakepath\\",
                    ""
                  )}`
                : Parser("Attach a file <span>(optional)</span>")}
            </Filename>
            {fileAttached && (
              <Fragment>
                <ReactTooltip
                  html
                  place="left"
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                  id="remove-attachment-tooltip"
                />
                <RemoveAttachment
                  onClick={this.removeAttachment}
                  data-tip="Remove this attachment"
                  data-for="remove-attachment-tooltip"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </RemoveAttachment>
              </Fragment>
            )}
          </Field>
          <ButtonsWrapper className="animated fadeInUp delay-4">
            <Button
              secondary
              buttonName="Cancel"
              dataTestId="accept-job-offer-cancel-button"
              onClickAction={handleCancel}
            />
            {this.handleAcceptButton(isSalaryFilled, finalSalaryAccepted)}
          </ButtonsWrapper>
        </Form>
      </Fragment>
    );
  }
}

Accept.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  candidate: PropTypes.string.isRequired,
  closeActionModalAfterSubmit: PropTypes.func.isRequired,
  cardData: PropTypes.shape({
    candidate: PropTypes.string,
    agent: PropTypes.string,
    updateStatusLink: PropTypes.string,
  }).isRequired,
  fetchKanbanData: PropTypes.func.isRequired,
  jobCurrencySymbol: PropTypes.string.isRequired,
};

export default Accept;
